/**
 * Player-facing strings for server status and the errors that fall out of it, so every
 * screen that mentions offline, maintenance or a refused sign-in uses the same words
 * (docs/CONTEXT.md "Writing and naming guidelines", D3).
 *
 * British English, plain words, no exclamation marks, no emoji. Amber is for offline and
 * maintenance, which are recoverable; red is only for the boot error, which has stopped.
 */
import { lower, t } from '../idle/terms.ts';

// --- Sign-in ---------------------------------------------------------------------------

export const SIGNIN_NO_CONNECTION = 'No connection to the server.';
export const SIGNIN_MAINTENANCE = 'The server is being updated. Try again in a minute.';
export const SIGNIN_ADDRESS_REFUSED = 'That address was not accepted.';
export const SIGNIN_TOO_MANY_LINKS = 'Too many links asked for. Try again in an hour.';
export const SIGNIN_PASSKEY_NOT_CHOSEN =
  'No passkey was chosen. If you have not made one yet, sign in by email and add one.';
export const SIGNIN_PASSKEY_UNSUPPORTED = 'This browser does not support passkeys here.';
export const SIGNIN_PASSKEY_START_FAILED = 'The server could not start a passkey. Try again.';
export const SIGNIN_START_FAILED = 'The server could not start sign-in. Try again.';

/**
 * The code that comes in the same email as the link (item 79). A link opens the default
 * browser, so it cannot sign in an installed app; the code can, wherever the game runs.
 */
export const SIGNIN_CODE_LABEL = 'Or type the code from the email';
export const SIGNIN_CODE_ACTION = 'Sign in with the code';
export const SIGNIN_EMAIL_ACTION = 'Email me a link and a code';
export const SIGNIN_CODE_SENT =
  'Check your email for a link and a six digit code. Either one works once, and both last 15 minutes.';
export const SIGNIN_CODE_REFUSED = 'That code is wrong, or it has been used or has expired.';
export const SIGNIN_TOO_MANY_CODES = 'Too many codes tried. Try again in an hour.';
/** The sheet says why a code exists at all, since passkeys are the first offer. */
export const SIGNIN_CODE_NOTE =
  'A link opens your browser, so use the code if the game is installed on this device.';

/** The world runs on the server whether the player is there or not (D1, D2, item 77). */
export const AWAY_JOBS_CONTINUE = 'Jobs finish whether or not you are here.';

/**
 * A SecurityError means the address bar does not match the relying party, which happens on
 * the LAN when the page is opened by IP address rather than the name the server was told.
 * `portSuffix` is `:PORT` or empty, kept out of this module so it stays free of `location`.
 */
export function signinPasskeyWrongAddress(rpId: string, portSuffix: string): string {
  return `Passkeys only work when the address is ${rpId}. Open https://${rpId}${portSuffix} and try again.`;
}

/**
 * D190. The same thing on a device that has used a passkey or an email. No guest world is
 * made here, so the copy names the two ways back in rather than leaving the player to look
 * for them.
 */
export const sessionEndedSignedIn = (): string =>
  `Your session ended. Sign in with your passkey or your email to get your ${t('world')} back.`;

/** The sign-in sheet's own line on a device that has signed in before but has no session. */
export const signinSessionEndedNote = (): string =>
  `Your session ended. Your ${t('world')} is on the server, not in this browser. Sign in to reach it.`;

// --- What a press on a disabled control says (D235) ----------------------------------------
// Each one names the thing that would lift it (D180), for the controls that had no reason.
/** A request is on its way, so the control waits for the answer. */
export const REASON_WAIT = 'Wait for this to finish';
/** A new world cannot start without a name. */
export const REASON_NEEDS_WORLD_NAME = 'Needs a name';
/** Sign-in by email, before an address is typed. */
export const REASON_NEEDS_EMAIL = 'Needs your email address';
/** Sign-in by code, before the six figures are typed. */
export const REASON_NEEDS_CODE = 'Needs the code from the email';

// --- Sheets (D63) ----------------------------------------------------------------------

/** One word closes every sheet and the tile panel, in the same place on all of them. */
export const SHEET_CLOSE = 'Close';

/**
 * D176. The world turns in four steps, so the zoom cluster holds one button that steps
 * round rather than a pair. The word on it is what it does; the name a screen reader reads
 * says what turns, because the cluster's other names all say so too.
 */
export const MAP_TURN_BUTTON = 'Turn';
export const mapTurnLabel = (): string => `Turn the ${t('world')}`;

/**
 * D178. The world can also be seen from directly above, and the button that does it sits in
 * the zoom cluster beside Turn. One word on it, because the cluster's buttons are small,
 * and it stays the same word in both states: the button is shown pressed while the world is
 * seen from above, so what it says is the view it gives rather than a word that changes
 * under the finger. The name a screen reader reads is the whole sentence, and the pressed
 * state is what tells it which way the world is being seen.
 */
export const MAP_PLAN_BUTTON = 'Above';
export const mapPlanLabel = (): string => `See the ${t('world')} from above`;

/**
 * D233. The compass says where north is in words, for a screen reader, from the angle the
 * map projects it to (degrees clockwise from straight up), to the nearest eighth of a turn.
 */
const COMPASS_WAYS = [
  'up',
  'up and to the right',
  'to the right',
  'down and to the right',
  'down',
  'down and to the left',
  'to the left',
  'up and to the left',
] as const;
export const compassLabel = (deg: number): string =>
  `North is ${COMPASS_WAYS[((Math.round(deg / 45) % 8) + 8) % 8]}`;
export const COMPASS_NORTH = 'N';
// --- The player guide (D79) --------------------------------------------------------------

/** The page the guide opens at, and where Back ends up: the contents. */
export const GUIDE_CONTENTS_PAGE = 'README.md';
export const GUIDE_TITLE = 'Guide';
/** A page name that is not in this build. Only reachable from a stale link or a hint. */
export const GUIDE_MISSING = 'That page is not in this build of the guide. Try the contents.';

// --- Deleting the account (D417) ---------------------------------------------------------

export const DELETE_ACCOUNT = 'Delete account';
export const DELETE_ACCOUNT_GOES = 'This is deleted:';
export const deleteAccountLoses = (): readonly string[] => [
  `All your ${t('worlds')}`,
  'Your passkeys, email and sign-in links',
  'Your notifications',
];
export const DELETE_ACCOUNT_TYPE = 'Type delete to confirm.';
export const DELETE_ACCOUNT_WORD = 'delete';
export const DELETE_ACCOUNT_INPUT = 'Confirm word';
export const DELETE_ACCOUNT_NEEDS_WORD = 'Type delete first';
export const DELETE_ACCOUNT_FAILED = 'Your account was not deleted. Try again.';

// --- Deleting a world (D221) --------------------------------------------------------------

/** D221: the action beside a world in the Worlds card. */
export const DELETE_WORLD = 'Delete';
/** D221: its accessible name, so a screen reader hears which world. */
export const deleteWorldLabel = (name: string): string => `Delete ${name}`;
/** D221: the confirm sheet's title. */
export const deleteWorldTitle = (name: string): string => `Delete ${name}`;
export const DELETE_WORLD_UNDO = 'This cannot be undone.';
export const DELETE_WORLD_GOES = 'This goes with it:';
/** D337: what goes with an idle world. */
export const deleteIdleWorldLoses = (): readonly string[] => [
  `Its land, crops, ${t('hands')} and buildings`,
  `Its ${t('coins')}, goals and orders`,
  `Its place in the ${lower(t('region'))}`,
];
/** D221: a world's Heritage goes with it, with the amount when it has any. */
export const deleteWorldHeritage = (heritage: number): string =>
  heritage > 0 ? `Its Heritage goes with it: ${heritage}` : 'Its Heritage goes with it';
export const deleteWorldKeeps = (): string =>
  `Your account and your other ${t('worlds')} stay as they are.`;
/** D221: the prompt above the box the name is typed into. */
export const deleteWorldTypeName = (name: string): string => `Type ${name} to delete it.`;
export const DELETE_WORLD_CANCEL = 'Cancel';
/** D363: the sheet's own wording (app word, not the theme's). */
export const DELETE_WORLD_INPUT = 'World name';
export const DELETE_WORLD_NEEDS_NAME = 'Type the world name first';
export const DELETE_WORLD_CONFIRM = 'Delete world';
/** D221: nothing was deleted, or the delete stopped part way and a retry will finish it. */
export const deleteWorldFailed = (): string => `The ${t('world')} was not deleted. Try again.`;
